/*
    Recoloring a Lottie animation by exact color. A color is an array of channels in 0..1,
    `[r, g, b]` or `[r, g, b, a]`, either static (`c: {a: 0, k: [...]}`) or keyframed
    (`c: {a: 1, k: [{t, s: [...], e?: [...]}, ...]}`). Only the RGB channels of fills and strokes are
    replaced; alpha, keyframe timing and easing, and everything else stay exactly as drawn. Gradients
    and text colors are left alone - the success animation has neither.
*/

export type LottieRgb = [number, number, number];

type JsonObject = Record<string, unknown>;

const isChannels = (value: unknown): value is number[] => (
    Array.isArray(value)
    && (value.length === 3 || value.length === 4)
    && value.every(channel => typeof channel === 'number')
);

const toByte = (channel: number) => Math.round(Math.min(1, Math.max(0, channel)) * 255);

/** "#27A360" for a Lottie color, matched on 8-bit channels; undefined for anything else. */
export const lottieColorHex = (value: unknown): string | undefined => (
    isChannels(value)
        ? `#${value.slice(0, 3).map(channel => toByte(channel).toString(16).padStart(2, '0')).join('')}`.toUpperCase()
        : undefined
);

/** [channels] with the RGB of its replacement and its own alpha - a new array, never shared between shapes. */
const swap = (channels: number[], replacements: Record<string, LottieRgb>): number[] => {
    const replacement = replacements[lottieColorHex(channels) ?? ''];
    return replacement ? [...replacement, ...channels.slice(3)] : channels;
};

const recolorProperty = (property: JsonObject, replacements: Record<string, LottieRgb>) => {
    const {k} = property;
    if (isChannels(k)) {
        property.k = swap(k, replacements);
        return;
    }
    if (!Array.isArray(k)) return;

    k.forEach(keyframe => {
        if (!keyframe || typeof keyframe !== 'object') return;
        const frame = keyframe as JsonObject;
        if (isChannels(frame.s)) frame.s = swap(frame.s, replacements);
        if (isChannels(frame.e)) frame.e = swap(frame.e, replacements);
    });
};

/**
 * A deep copy of [animation] - the player annotates what it is given - with every fill and stroke
 * whose color is a key of [replacements] (`#RRGGBB`) recolored.
 */
export const recolorLottie = <T extends object>(animation: T, replacements: Record<string, LottieRgb>): T => {
    const copy = JSON.parse(JSON.stringify(animation)) as T;
    const byHex = Object.fromEntries(Object.entries(replacements).map(([hex, rgb]) => [hex.toUpperCase(), rgb]));

    const visit = (value: unknown): void => {
        if (Array.isArray(value)) {
            value.forEach(visit);
            return;
        }
        if (!value || typeof value !== 'object') return;

        const node = value as JsonObject;
        if ((node.ty === 'fl' || node.ty === 'st') && !!node.c && typeof node.c === 'object') {
            recolorProperty(node.c as JsonObject, byHex);
        }
        Object.values(node).forEach(visit);
    };

    visit(copy);
    return copy;
};
